<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Service Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.service_name')"
                v-model.trim="filterOptions.service_name"
              />
              <!-- End:: Service Name Input -->

              <!-- Start:: Provider Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.provider_name')"
                v-model.trim="filterOptions.provider_name"
              />
              <!-- End:: Provider Name Input -->

              <!-- Start:: Office Type Input -->
              <base-select-input
                col="4"
                :optionsList="officeTypes"
                :placeholder="$t('PLACEHOLDERS.office_type')"
                v-model="filterOptions.office_type"
              />
              <!-- End:: Office Type Input -->

              <!-- Start:: Activation Status Input -->
              <base-select-input
                col="4"
                :optionsList="activation_statuses"
                :placeholder="$t('PLACEHOLDERS.activation_status')"
                v-model="filterOptions.activation_status"
              />
              <!-- End:: Activation Status Input -->

              <!-- Start:: From Date Input -->
              <base-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from')"
                v-model.trim="filterOptions.from"
              />

              <!-- Start:: To Date Input -->
              <base-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to')"
                v-model.trim="filterOptions.to"
              />
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.additional_services") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>

        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>

        <!-- Start:: Service Name -->
        <template v-slot:[`item.service_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.name }}</h6>
        </template>

        <!-- Start:: Provider Name -->
        <template v-slot:[`item.provider_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.provider?.name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.provider.name }}</h6>
        </template>

        <!-- Start:: Office Type -->
        <template v-slot:[`item.office_type`]="{ item }">
          <h6 class="text-danger" v-if="!item.provider?.exhibitor_type_trans">
            {{ $t("TABLES.noData") }}
          </h6>
          <v-chip 
            :color="getOfficeTypeColor(item.provider?.exhibitor_type)" 
            text-color="white" 
            small
            v-else
          >
            {{ item.provider.exhibitor_type_trans }}
          </v-chip>
        </template>

        <!-- Start:: Price -->
        <template v-slot:[`item.price`]="{ item }">
          <span>{{ item.price }} {{ item.currency }}</span>
        </template>

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="getStatusColor(item.status)" text-color="white" small>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Start:: Activation -->
        <template v-slot:[`item.is_active`]="{ item }">
          <div class="activation" dir="ltr" style="z-index: 1">
            <v-switch
              class="mt-2"
              color="success"
              v-model="item.is_active"
              hide-details
              @change="toggleStatus(item)"
            ></v-switch>
          </div>
        </template>

        <!-- Start:: Approval Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <span>{{ formatDate(item.created_at) }}</span>
        </template>

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('additionalservices show', 'additionalservices')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="$can('additionalservices updateprice', 'additionalservices')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.edit") }}</span>
              </template>
              <button class="btn_edit" @click="editItem(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="$can('additionalservices delete', 'additionalservices')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>
          </div>
        </template>

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{
                  $t("TITLES.DeleteConfirmingMessage", {
                    name: itemToDelete.name || "هذا العنصر",
                  })
                }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteItem">{{
                  $t("BUTTONS.ok")
                }}</v-btn>
                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllAdditionalServices",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    activation_statuses() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.is_active"),
          value: "1",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.inactive"),
          value: "0",
        },
      ];
    },

    officeTypes() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.individual"),
          value: "individual",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.local_office"),
          value: "local_office",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.global_office"),
          value: "global_office",
        },
        {
          id: 4,
          name: this.$t("PLACEHOLDERS.broker"),
          value: "broker",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        service_name: null,
        provider_name: null,
        office_type: null,
        activation_status: null,
        from: null,
        to: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.service_name"),
          value: "service_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.provider_name"),
          value: "provider_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.office_type"),
          value: "office_type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.price"),
          value: "price",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.is_active"),
          value: "is_active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.approval_date"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data

      // Start:: Dialogs
      dialogDelete: false,
      itemToDelete: null,
      // End:: Dialogs
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    // Start:: Filter Methods
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/additional-services/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.service_name = null;
      this.filterOptions.provider_name = null;
      this.filterOptions.office_type = null;
      this.filterOptions.activation_status = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/additional-services/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    // Start:: Table Methods
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "additional-services",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.service_name,
            provider_name: this.filterOptions.provider_name,
            exhibitor_type: this.filterOptions.office_type?.value,
            is_active: this.filterOptions.activation_status?.value,
            from_date: this.filterOptions.from,
            to_date: this.filterOptions.to,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },

    // Start:: Status Methods
    getStatusColor(status) {
      switch (status) {
        case "pending":
          return "orange";
        case "approved":
          return "green";
        case "rejected":
          return "red";
        default:
          return "grey";
      }
    },

    getStatusText(status) {
      switch (status) {
        case "pending":
          return this.$t("PLACEHOLDERS.pending");
        case "approved":
          return this.$t("PLACEHOLDERS.approved");
        case "rejected":
          return this.$t("PLACEHOLDERS.rejected");
        default:
          return this.$t("PLACEHOLDERS.unknown");
      }
    },

    getOfficeTypeColor(type) {
      switch (type) {
        case "individual":
          return "blue";
        case "local_office":
          return "green";
        case "global_office":
          return "purple";
        case "broker":
          return "orange";
        default:
          return "grey";
      }
    },

    formatDate(dateString) {
      if (!dateString) return this.$t("TABLES.noData");
      const date = new Date(dateString);
      return date.toLocaleDateString(this.getAppLocale === 'ar' ? 'ar-EG' : 'en-US');
    },

    // Start:: CRUD Methods
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },

    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `additional-services/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.tableRows = this.tableRows.filter((item) => {
          return item.id != this.itemToDelete.id;
        });
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(error.response.data.message);
      }
    },

    async toggleStatus(item) {
      try {
        await this.$axios({
          method: "POST",
          url: `additional-services/${item.id}/activate`,
        });
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.changeActivation"));
      } catch (error) {
        this.$message.error(error.response.data.message);
      }
    },

    showItem(item) {
      this.$router.push({ path: `/additional-services/show/${item.id}` });
    },

    editItem(item) {
      this.$router.push({ path: `/additional-services/edit/${item.id}` });
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
  },
};
</script>

<style>
span.submit_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #f6a513;
  display: flex;
  justify-content: center;
  align-items: center;
}

.price-tag {
  background-color: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
}
</style>
